/**
 * Theme API endpoints.
 *
 * A site starts on a bundled theme (read-only). Forking copies it into a
 * site-owned theme whose Tera templates + static assets can be edited here.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

/** Where the site's active theme lives */
export type ThemeSource = 'bundled' | 'custom';

/** A file inside the site's custom theme */
export interface ThemeFile {
  path: string;
  size: number;
}

/** Active theme info. `files` is empty while source is 'bundled'. */
export interface SiteTheme {
  active_theme: string;
  /** Resolved applied blueprint, including an imported legacy fork. */
  account_theme_id?: number | null;
  source: ThemeSource;
  forked_from: string | null;
  files: ThemeFile[];
  /**
   * Paths this theme inherited from the bundled theme it forked from. Anything in
   * `files` that is NOT here was added by the user (AB#650: Standard vs Custom files).
   */
  origin_files: string[];
  /** Inherited files whose bytes differ from their bundled original. */
  modified_files?: string[];
}

/**
 * A bundled ("stock") theme — one that ships with WordPuppi. AB#644: `category`
 * and `description` come from the theme's optional `theme.json`, and are null
 * for a theme that has none. Stock themes live in the image, so they are always
 * available and can never be deleted.
 */
export interface BundledTheme {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  /**
   * The theme's file tree — read-only (see `sitesApi.bundledThemeFile`).
   * Only present when the list was fetched with `files=1`.
   */
  files?: ThemeFile[] | null;
}

/**
 * Fork body: EXACTLY ONE source — `from` (a bundled theme id) or
 * `from_account_theme_id` (an account/user blueprint, copied into this site).
 */
export interface ForkThemeRequest {
  from?: string;
  from_account_theme_id?: number;
}

/** How a file's bytes are carried over the wire. Absent = utf8. */
export type FileEncoding = 'utf8' | 'base64';

/** Contents of a single theme file. Binary static files come back base64-encoded. */
export interface ThemeFileContent {
  path: string;
  content: string;
  encoding?: FileEncoding;
  content_sha256: string;
}

/**
 * Encode a multi-segment theme path for the `{*path}` route segment.
 * Each segment is escaped separately so the slashes survive.
 */
function encodeThemePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/** Theme API */
export const themesApi = {
  /** Get the site's active theme + its files (empty when bundled) */
  get: (siteSlug: string) => api.get<ApiResponse<SiteTheme>>(`/v1/sites/${siteSlug}/theme`),

  /** Fork a bundled theme — or copy a blueprint — into a site-owned copy */
  fork: (siteSlug: string, data: ForkThemeRequest) =>
    api.post<ApiResponse<SiteTheme>>(`/v1/sites/${siteSlug}/theme/fork`, data),

  /** Read one theme file */
  getFile: (siteSlug: string, path: string) =>
    api.get<ApiResponse<ThemeFileContent>>(
      `/v1/sites/${siteSlug}/theme/files/${encodeThemePath(path)}`,
    ),

  /**
   * Write one theme file. Throws ApiError (422) carrying the Tera parse error.
   * Pass `encoding: 'base64'` to write a binary asset (font, image) — the server
   * decodes it before the 5MB cap.
   */
  putFile: (siteSlug: string, path: string, content: string, encoding: FileEncoding | undefined, expectedContent: string) =>
    api.put<ApiResponse<{ content_sha256: string }>>(`/v1/sites/${siteSlug}/theme/files/${encodeThemePath(path)}`, {
      content,
      ...(encoding ? { encoding } : {}),
      expected_content: expectedContent,
    }),

  /** Delete one theme file. Throws ApiError (422) if it breaks the template parse. */
  deleteFile: (siteSlug: string, path: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/theme/files/${encodeThemePath(path)}`),

  /** AB#945: templates a page may use (`page.html`, `wide.html`, `pages/<slug>.html`…). */
  pageTemplates: (siteSlug: string) =>
    api.get<ApiResponse<string[]>>(`/v1/sites/${siteSlug}/theme/page-templates`),

  /** Discard the custom theme; the site reverts to the bundled theme it forked from */
  discard: (siteSlug: string) => api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/theme`),
};

// ============================================================================
// Account themes (AB#616) — reusable, account-scoped copies of a theme that any
// of the account's sites can adopt via `account_theme_id` on its settings.
// ============================================================================

/**
 * A saved account theme. `base` records where it was copied from:
 * a bundled theme id (`"wordpuppi-marketing"`), a site (`"site:<slug>"`), or null.
 */
export interface AccountTheme {
  id: number;
  name: string;
  base: string | null;
  /** `user` = the caller's own; `account` = every seat; `site` = one site (AB#650). */
  scope: AccountThemeScope;
  /** Set on `site`-scoped rows only. */
  site_id?: number;
  /** Paths inherited from the theme it was copied from (see SiteTheme.origin_files). */
  origin_files: string[];
  /** Inherited files whose bytes differ from their bundled original. */
  modified_files?: string[];
  files: ThemeFile[];
  /** How many sites build with this blueprint. Deleting one in use 409s (`in_use`). */
  in_use_by: number;
  created_at: string;
  updated_at: string;
}

/**
 * Who owns a blueprint — also the column it appears in on the Blueprints page.
 * `site` rows are owned by one site (`site_id`) and listed by `?site=<slug>`.
 */
export type AccountThemeScope = 'user' | 'account' | 'site';

/**
 * Create body: `name` plus EXACTLY ONE source — `from_bundled` (a bundled theme
 * id) or `from_site` (an owned site slug, copying its current theme).
 */
export interface CreateAccountThemeRequest {
  name: string;
  from_bundled?: string;
  from_site?: string;
  /** Defaults to `user` server-side. */
  scope?: AccountThemeScope;
  /** Site whose account the theme belongs to (required for `scope: 'account'`). */
  site?: string;
}

/** Duplicate body — every field optional (name defaults to "<name> copy"). */
export interface DuplicateAccountThemeRequest {
  name?: string;
  scope?: AccountThemeScope;
  site?: string;
}

export const accountThemesApi = {
  /**
   * List themes visible here (newest first). Pass the site slug to also get the
   * `account`-scoped themes of THAT site's account; without it only `user` themes.
   */
  list: (site?: string) =>
    api.get<ApiResponse<AccountTheme[]>>(
      site ? `/account/themes?site=${encodeURIComponent(site)}` : '/account/themes',
    ),

  /** Create a theme by copying a bundled theme or a site's current theme. */
  create: (data: CreateAccountThemeRequest) =>
    api.post<ApiResponse<AccountTheme>>('/account/themes', data),

  /** Copy an existing theme (optionally into the other scope). */
  duplicate: (id: number, data: DuplicateAccountThemeRequest = {}) =>
    api.post<ApiResponse<AccountTheme>>(`/account/themes/${id}/duplicate`, data),

  /** Rename a theme (400 on empty name, 404 if not the caller's). */
  rename: (id: number, name: string) =>
    api.put<ApiResponse<AccountTheme>>(`/account/themes/${id}`, { name }),

  /** Delete a theme. Sites referencing it fall back to their bundled theme. */
  remove: (id: number) => api.delete<ApiResponse<null>>(`/account/themes/${id}`),

  // File endpoints mirror the site ones byte-for-byte (same bodies, same 422s).

  /** Read one file of an account theme. */
  getFile: (id: number, path: string) =>
    api.get<ApiResponse<ThemeFileContent>>(`/account/themes/${id}/files/${encodeThemePath(path)}`),

  /** Write one file. Same body as the site one, base64 encoding included. */
  putFile: (id: number, path: string, content: string, encoding: FileEncoding | undefined, expectedContent: string) =>
    api.put<ApiResponse<{ content_sha256: string }>>(`/account/themes/${id}/files/${encodeThemePath(path)}`, {
      content,
      ...(encoding ? { encoding } : {}),
      expected_content: expectedContent,
    }),

  /** Delete one file. Throws ApiError (422) if it breaks the template parse. */
  deleteFile: (id: number, path: string) =>
    api.delete<ApiResponse<null>>(`/account/themes/${id}/files/${encodeThemePath(path)}`),
};
